<template>
  <app-layout
    :state="state"
    header-type="landing"
  >
    <template #header>
      <div
        class="flex items-baseline"
        :class="{
          'pb-6': tabs.length > 1
        }"
      >
        <div class="flex-1">
          <h1 class="text-3xl md:text-4xl font-light">
            {{ state.funding_pot.name }}
          </h1>
          <p class="text-xl md:text-2xl font-semibold">
            Programme Settings & Configuration
          </p>
        </div>
        <div v-if="$slots.headerAction" class="flex-no-shrink ml-8 w-48">
          <slot name="headerAction"></slot>
        </div>
      </div>
    </template>
    <app-header>
      <div class="flex flex-wrap items-end">
        <div class="flex-1">
          <icon-link
            :href="
              route(
                'funding.competitions.index',
                state.competition.funding_pots[0].id
              )
            "
            icon="arrow-thin-left-circle"
            class="icons-secondary"
          >
            Funding pot competitions
          </icon-link>
          <h1 class="leading-tight">
            {{ state.competition.title || "New" }} Competition
            <app-badge
              v-if="!competitionPublishDate"
              type="neutral"
              class="align-text-top"
            >
              Draft
            </app-badge>
          </h1>
          <p class="text-sm text-grey-600">
            {{ state.competition.funding_pots[0].name }}
          </p>
        </div>
        <div class="w-48 text-right">
          <app-button
            :disabled="!hasChanged"
            colour="confirm"
            @click="saveCompetition"
          >
            Save
          </app-button>
        </div>
      </div>
      <template #navigation>
        <ul v-if="tabs">
          <li>
            <a
              :href="route('funding.competitions.index', state.funding_pot.id)"
            >
              &leftarrow; Back to dashboard
            </a>
          </li>
          <li>
            <span class="w-px mx-6 bg-grey-300 block h-full"></span>
          </li>
          <li v-for="(tab, index) in tabs" :key="index">
            <a
              :href="'#' + tab.hash"
              :class="{
                'is-active': tab.hash === active
              }"
              @click.prevent="setActive(tab.hash)"
            >
              {{ tab.name }}
            </a>
          </li>
        </ul>
      </template>
      <template #tertiary>
        <portal-target name="competition-edit-tertiary" slim />
      </template>
    </app-header>
    <div class="container mt-6">
      <keep-alive>
        <component
          :is="`competition-edit-${active}`"
          :state="state"
          :fields="fields"
          @update:field="updateField"
        />
      </keep-alive>
    </div>
  </app-layout>
</template>

<script>
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
const editableFields = [
  "competition",
  "contracts",
  "available_funding_pots"
];
export default {
  name: "FundingCompetitionsView",
  data() {
    return {
      showWaves: false,
      active: "templates",
      hasChanged: false,
      hasAutoSave: false,
      fields: JSON.parse(
        window.localStorage.getItem(this.state.competition.uuid)
      )
    };
  },
  computed: {
    competitionType() {
      return this.state.competition?.type?.name ?? false;
    },
    tabs() {
      return [
        {
          name: "Settings",
          hash: "settings"
        },
        {
          name: "Application",
          hash: "application"
        },
        {
          name: "Milestones",
          hash: "milestones"
        },
        {
          name: "Templates",
          hash: "templates"
        },
      ];
    },
    competitionPublishDate() {
      if (!this.state.competition.published_at) return;
      return format(
        parseISO(this.state.competition.published_at),
        "do MMMM yyyy"
      );
    }
  },
  watch: {
    fields: {
      handler() {
        this.hasChanged = !isEqual(
          this.fields,
          pick(this.state, editableFields)
        );
        if (!window.localStorage) return;
        if (this.hasChanged) {
          window.localStorage.setItem(
            this.state.competition.uuid,
            JSON.stringify(this.fields)
          );
        } else {
          window.localStorage.removeItem(this.state.competition.uuid);
        }
      },
      deep: true
    }
  },
  created() {
    this.loadTabWithHash(window.location.hash);
    this.populateEditableFields();
  },
  methods: {
    removeAutoSave() {
      window.localStorage.removeItem(this.state.competition.uuid);
      return this.populateEditableFields();
    },
    populateEditableFields() {
      if (window.localStorage.getItem(this.state.competition.uuid) !== null)
        return;
      editableFields.map(field => {
        const f = this.state[field];
        // Handle Array setting
        if (Array.isArray(f))
          return (this.fields = { ...this.fields, ...{ [field]: [...f] } });
        // Handle Object setting
        if (typeof f === "object")
          return (this.fields = { ...this.fields, ...{ [field]: { ...f } } });
        // Handle the rest
        return (this.fields = { ...this.fields, ...{ [field]: f } });
      });
    },
    loadTabWithHash(hash) {
      if (!hash) return;
      hash = hash.replace("#", "");
      return this.setActive(hash);
    },
    setActive(index) {
      window.location.hash = this.tabs[index] ? this.tabs[index].hash : index;
      this.scrollTop();
      this.saveApplication;
      this.active = index;
    },
    updateField(payload) {
      let field = Object.keys(payload)[0].split(".");
      return this.$set(
        this.fields[field[0]],
        [field[1]],
        Object.values(payload)[0]
      );
    },
    saveCompetition() {
      const competition = this.state.competition.uuid;
      const funding = this.state.funding_pot.id;
      const url = route("funding.competitions.update", {
        funding,
        competition
      });
      axios.patch(url, this.fields).then(() => {
        window.localStorage.removeItem(competition);
        return Turbolinks.visit(url);
      });
    }
  }
};
</script>
